import React from 'react';
import MarcEtapa from './MarcEtapa';
import { useTranslation } from 'react-i18next';
import { PlusIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  ESTATS, perCanvi, netejaAccions, recompte, avisos, potTancarElCicle,
} from '../../accionsSSM';

// L'ETAPA 7: L'ACCIÓ.
//
// Les regles són a `src/accionsSSM.js`, amb proves pròpies (`npm run prova:accions`).
//
// **Rep una llista tancada**: els canvis que a l'etapa 6 han passat els dos eixos. Aquí no es
// decideix res de nou; es converteix cada acord en **qui fa què, quan, i com se sabrà**.
//
// I acaba dient el que la majoria d'eines es deixen: **actuar canvia la situació**. Quan les
// accions passen, la situació ja no és la que es va explorar a l'etapa 1, i el que hi ha és una
// situació nova per tornar a mirar. Per això aquesta pantalla no acaba amb un «ja està».

const ImplementationStage = ({ data, onUpdate }) => {
  const { t } = useTranslation();
  const impl = data.implementation || {};
  const grups = perCanvi(data);
  const problemes = avisos(data);
  const r = recompte(data);

  const desa = (accions) => onUpdate({ implementation: { ...impl, actions: accions } });
  const totes = impl.actions || [];

  const afegeix = (changeId) => desa([...totes,
    { id: `ac_${Date.now()}`, changeId, text: '', who: '', when: '', how: '', status: 'todo' }]);

  const posa = (id, camp, valor) =>
    desa(totes.map((a) => (a.id === id ? { ...a, [camp]: valor } : a)));

  const treu = (id) => desa(totes.filter((a) => a.id !== id));

  return (
    <MarcEtapa className="p-6">
      <div className="max-w-5xl mx-auto space-y-5">
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <div className="flex items-start justify-between gap-4">
            <p className="text-sm text-gray-700">{t('ssm.acQue')}</p>
            {r.total > 0 && (
              <span className="flex-shrink-0 text-xs text-gray-500 tabular-nums">
                {t('ssm.acRecompte', { fetes: r.fetes, total: r.total })}
              </span>
            )}
          </div>
        </div>

        {problemes.length > 0 && (
          <ul className="space-y-1.5">
            {problemes.map((p, i) => (
              <li key={i} className="flex items-center gap-3 px-4 py-2.5 rounded-md bg-white
                                     border border-warn-400 text-sm text-gray-700">
                <span>{t(p.clau, { n: p.n })}</span>
                {p.clau === 'ssm.acOrfes' && (
                  <button type="button" onClick={() => desa(netejaAccions(data))}
                    className="ml-auto text-xs font-medium text-primary-700 hover:underline">
                    {t('ssm.cpNetejaOrfes')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {grups.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-md p-6">
            <p className="text-gray-700">{t('ssm.acCapAcord')}</p>
          </div>
        ) : grups.map(({ canvi, accions }) => (
          <div key={canvi.id} className="bg-white border border-gray-200 rounded-md p-4">
            <div className="flex items-baseline gap-2 mb-3">
              <h3 className="text-sm font-semibold text-gray-900">{canvi.text || t('ssm.untitled')}</h3>
              {canvi.mena && (
                <span className="text-xs text-gray-500">({t(`ssm.cvMena_${canvi.mena}`)})</span>
              )}
            </div>

            {accions.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                      <th className="px-2 py-1.5 font-medium">{t('ssm.acQue2')}</th>
                      <th className="px-2 py-1.5 font-medium w-40">{t('ssm.acQui')}</th>
                      <th className="px-2 py-1.5 font-medium w-32">{t('ssm.acQuan')}</th>
                      <th className="px-2 py-1.5 font-medium w-56">{t('ssm.acCom')}</th>
                      <th className="px-2 py-1.5 font-medium w-44">{t('ssm.acEstat')}</th>
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {accions.map((a) => (
                      <tr key={a.id} className="border-b border-gray-100">
                        {[['text', 'acQue2Ph'], ['who', 'acQuiPh'], ['when', 'acQuanPh'], ['how', 'acComPh']]
                          .map(([camp, ph]) => (
                          <td key={camp} className="px-2 py-1.5">
                            <input value={a[camp] || ''} onChange={(e) => posa(a.id, camp, e.target.value)}
                              placeholder={t(`ssm.${ph}`)}
                              className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm
                                         focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                          </td>
                        ))}
                        <td className="px-2 py-1.5">
                          {/* Horitzontals i sense embolicar: en una columna de 144 px els tres
                              botons queien un sota l'altre i la fila creixia el triple d'alta. */}
                          <div className="flex gap-1 flex-nowrap">
                            {ESTATS.map((e) => (
                              <button key={e} type="button" onClick={() => posa(a.id, 'status', e)}
                                className={`px-1.5 py-1 rounded-md text-[11px] font-medium border
                                            whitespace-nowrap transition-colors ${
                                  (a.status || 'todo') === e
                                    ? 'bg-primary-700 text-white border-primary-700'
                                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                                {t(`ssm.acE${e}`)}
                              </button>
                            ))}
                          </div>
                        </td>
                        <td className="px-2 py-1.5">
                          <button type="button" onClick={() => treu(a.id)}
                            className="p-1 text-gray-400 hover:text-gray-600" aria-label={t('common.remove')}>
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button type="button" onClick={() => afegeix(canvi.id)}
              className="mt-3 flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:underline">
              <PlusIcon className="h-3.5 w-3.5" />{t('ssm.acAfegir')}
            </button>
          </div>
        ))}

        {/* EL BUCLE, que és el que la majoria d'eines es deixen. No és un «ja està»: la situació
            ja no és la mateixa que es va explorar a l'etapa 1, i el que hi ha és una situació
            nova per tornar a mirar. */}
        {potTancarElCicle(data) && (
          <div className="bg-primary-50 border border-primary-200 rounded-md p-4 flex items-start gap-3">
            <ArrowPathIcon className="h-5 w-5 text-primary-700 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-primary-900">{t('ssm.acBucle')}</h3>
              <p className="text-sm text-primary-800 mt-1">{t('ssm.acBucleWhy')}</p>
            </div>
          </div>
        )}
      </div>
    </MarcEtapa>
  );
};

export default ImplementationStage;
